import { useEffect, useRef, useState } from 'react';
import { Play, Pause, RotateCcw, Minus, Plus, Type } from 'lucide-react';

/**
 * Auto-scrolling teleprompter overlay rendered on top of the live webcam
 * preview while recording. The presenter reads the script as it scrolls past
 * the amber read-line. Pure DOM/rAF — no canvas, no effect on the recording.
 */
export const TeleprompterOverlay = ({ text }) => {
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(45);   // px / second
  const [fontSize, setFontSize] = useState(30);
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const rafRef = useRef(null);
  const lastRef = useRef(null);

  // Reset to top whenever the script text changes.
  useEffect(() => {
    offsetRef.current = 0;
    if (trackRef.current) trackRef.current.style.transform = 'translateY(0px)';
  }, [text]);

  useEffect(() => {
    const step = (t) => {
      if (lastRef.current == null) lastRef.current = t;
      const dt = (t - lastRef.current) / 1000;
      lastRef.current = t;
      if (playing && trackRef.current) {
        offsetRef.current += speed * dt;
        const max = Math.max(0, trackRef.current.scrollHeight);
        if (offsetRef.current > max) offsetRef.current = max;
        trackRef.current.style.transform = `translateY(-${offsetRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(rafRef.current); lastRef.current = null; };
  }, [playing, speed]);

  const restart = () => {
    offsetRef.current = 0;
    if (trackRef.current) trackRef.current.style.transform = 'translateY(0px)';
    setPlaying(true);
  };

  return (
    <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none" data-testid="teleprompter-overlay">
      {/* Scrolling reading area */}
      <div className="absolute inset-x-0 top-0 bottom-11 overflow-hidden bg-black/55">
        <div ref={trackRef} className="px-6 pt-[42%] will-change-transform">
          <p
            className="text-white font-semibold leading-relaxed text-center whitespace-pre-wrap"
            style={{ fontSize: `${fontSize}px`, textShadow: '0 2px 6px rgba(0,0,0,0.85)' }}
            data-testid="teleprompter-text"
          >
            {text}
          </p>
        </div>
        {/* read-line indicator */}
        <div className="absolute left-0 right-0 top-[42%] border-t-2 border-amber-400/70 pointer-events-none" />
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-1.5 p-1.5 bg-black/70 pointer-events-auto">
        <button type="button" onClick={() => setPlaying((p) => !p)}
          className="p-1.5 rounded-md text-white hover:bg-white/15" title={playing ? 'Pause' : 'Play'}
          data-testid="teleprompter-play-toggle">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button type="button" onClick={restart}
          className="p-1.5 rounded-md text-white hover:bg-white/15" title="Restart" data-testid="teleprompter-restart">
          <RotateCcw className="w-4 h-4" />
        </button>
        <span className="w-px h-5 bg-white/20 mx-0.5" />
        <span className="text-[10px] text-white/70">Speed</span>
        <button type="button" onClick={() => setSpeed((s) => Math.max(15, s - 10))}
          className="p-1.5 rounded-md text-white hover:bg-white/15" title="Slower" data-testid="teleprompter-slower">
          <Minus className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => setSpeed((s) => Math.min(120, s + 10))}
          className="p-1.5 rounded-md text-white hover:bg-white/15" title="Faster" data-testid="teleprompter-faster">
          <Plus className="w-3.5 h-3.5" />
        </button>
        <span className="w-px h-5 bg-white/20 mx-0.5" />
        <Type className="w-3.5 h-3.5 text-white/70" />
        <button type="button" onClick={() => setFontSize((f) => Math.max(18, f - 2))}
          className="p-1.5 rounded-md text-white hover:bg-white/15" title="Smaller text" data-testid="teleprompter-font-down">
          <Minus className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => setFontSize((f) => Math.min(56, f + 2))}
          className="p-1.5 rounded-md text-white hover:bg-white/15" title="Larger text" data-testid="teleprompter-font-up">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default TeleprompterOverlay;
